import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { ProductAnalyticsService, ProductEventParams, productErrorCode } from 'src/app/shared/services/product-analytics.service';
import { RenderBackend } from './render-backend';
import { RenderJob, RenderPackFile, RenderPackFiles, RenderPackSizes } from './render-flow.model';
import { RENDER_PACK_DEFAULT, RENDER_PACK_KEYS, RenderPackKey } from './render.data';

const STORAGE_KEY = '3dway.render-pack.v1';

export interface RenderPackSettings {
  remember: boolean;
  keys: RenderPackKey[] | null;
}

// The "Optimize for platforms" choice (designer, Figma 28563:27840, 2026-09-24) and the pack's
// files, for its card (render-pack-card: the downloads page's, and the library's download button
// opens it in a dialog). "Save configuration" keeps the choice for the signed-in user on this
// device.
@Injectable()
export class RenderPackService {
  constructor(
    private readonly backend: RenderBackend,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly productAnalytics: ProductAnalyticsService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  // "Save configuration": on unless the user turned it off; `keys` is the choice it keeps.
  public get settings(): RenderPackSettings {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null') as Partial<RenderPackSettings> | null;
      if (!stored || typeof stored !== 'object') {
        return { remember: true, keys: null };
      }
      const keys = Array.isArray(stored.keys) ? stored.keys.filter((key) => RENDER_PACK_KEYS.includes(key)) : null;
      return { remember: stored.remember !== false, keys: stored.remember === false ? null : keys };
    } catch {
      return { remember: true, keys: null };
    }
  }

  // What a pack holds when nothing else is said: the saved choice, else the videos as rendered.
  public get choice(): RenderPackKey[] {
    return this.settings.keys || [...RENDER_PACK_DEFAULT];
  }

  // Keeps the choice while `remember` is on; turning it off forgets the choice.
  public save(keys: ReadonlyArray<RenderPackKey>, remember: boolean): void {
    const settings: RenderPackSettings = { remember, keys: remember ? [...keys] : null };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
    } catch {
      // Private mode / blocked storage: the choice lasts for this page only.
    }
  }

  // The pack's files for the chosen platforms; nothing is saved yet. The product analytics count the
  // request, and a pack that did not come.
  public files(job: RenderJob, keys: ReadonlyArray<RenderPackKey>): Promise<RenderPackFiles> {
    const params: ProductEventParams = { pack: keys.join(','), count: keys.length };
    const modelId = job.source.kind === 'upload' && this.backend.kind !== 'mock' ? job.backendId : undefined;
    if (job.source.kind === 'sample') {
      params['sample'] = job.source.sampleId;
    }
    this.productAnalytics.track('download_requested', { modelId, params });
    return this.backend.getPack(job, keys).catch((error) => {
      this.productAnalytics.track('download_failed', { modelId, params: { ...params, reason: productErrorCode(error) } });
      throw error;
    });
  }

  // The source videos, one per click, in the ZIP's place.
  public standIn(job: RenderJob): boolean {
    return this.backend.packStandIn(job);
  }

  // What the videos weigh, or null where the backend cannot say (the table keeps its estimates).
  public sizes(job: RenderJob): Promise<RenderPackSizes | null> {
    return this.backend.packSizes(job);
  }

  // One file, in place. One per click: a browser lets a page start one download per user action
  // and blocks the rest as "multiple automatic downloads" (designer, 2026-10-16: Chrome kept the
  // landscape alone). The link carries Content-Disposition: attachment (renderAttachmentUrl / a
  // signed URL), so the browser saves the file and the page stays.
  public saveFile(file: RenderPackFile): void {
    const link = this.document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    link.rel = 'noopener';
    link.style.display = 'none';
    this.document.body.appendChild(link);
    link.click();
    link.remove();
    // The backend's ZIP, held in the page: let go once the browser has it.
    if (file.url.startsWith('blob:')) {
      setTimeout(() => URL.revokeObjectURL(file.url), 60000);
    }
  }

  private get storageKey(): string {
    return `${STORAGE_KEY}:${this.firebaseAuth.currentUser?.uid || 'guest'}`;
  }
}
